
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Plus, Edit, Save, X, Trash2 } from 'lucide-react';
import { useModelManagement } from '@/hooks/useModelManagement';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type ModelConfig = Database['public']['Tables']['model_configs']['Row'];
type ModelConfigInsert = Database['public']['Tables']['model_configs']['Insert'];

const AdminModelManagement = () => {
  const { models, loading, updateModel, addModel, deleteModel } = useModelManagement();
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ModelConfig>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newModel, setNewModel] = useState<ModelConfigInsert>({
    model_key: '',
    display_name: '',
    description: '',
    provider: '',
    category: '',
    priority: 100,
    max_batch_size: 4,
    supported_sizes: ['1024x1024'],
    supported_qualities: ['standard'],
    is_active: true,
    subscription_plan: 'normal'
  });

  const handleEditModel = (model: ModelConfig) => {
    console.log('Starting edit for model:', model.id);
    setEditingModel(model.id);
    setEditForm({
      model_key: model.model_key,
      display_name: model.display_name,
      description: model.description,
      provider: model.provider,
      category: model.category,
      priority: model.priority || 100,
      max_batch_size: model.max_batch_size,
      supported_sizes: model.supported_sizes,
      supported_qualities: model.supported_qualities,
      is_active: model.is_active,
      subscription_plan: model.subscription_plan || 'normal'
    });
  };

  const handleSaveModel = async () => {
    if (!editingModel || !editForm) return;
    console.log('Saving model changes:', editForm);
    await updateModel(editingModel, editForm);
    setEditingModel(null);
    setEditForm({});
  };

  const handleCancelEdit = () => {
    setEditingModel(null);
    setEditForm({});
  };

  const handleDeleteModel = async (id: string) => {
    const confirmed = confirm('Are you sure you want to delete this model?');
    if (confirmed) {
      await deleteModel(id);
    }
  };

  const handleAddModel = async () => {
    if (!newModel.model_key || !newModel.display_name || !newModel.category) {
      toast.error('Please fill in all required fields (model key, display name, category)');
      return;
    }
    
    // Auto-generate provider if not set
    if (!newModel.provider) {
      setNewModel({...newModel, provider: 'provider-1'});
    }
    
    console.log('Adding new model:', newModel);
    await addModel(newModel);
    setNewModel({
      model_key: '',
      display_name: '',
      description: '',
      provider: '',
      category: '',
      priority: 100,
      max_batch_size: 4,
      supported_sizes: ['1024x1024'],
      supported_qualities: ['standard'],
      is_active: true,
      subscription_plan: 'normal'
    });
    setShowAddForm(false);
  };

  const toggleModelStatus = async (id: string, currentStatus: boolean) => {
    console.log('Toggling model status:', id, 'from', currentStatus, 'to', !currentStatus);
    await updateModel(id, { is_active: !currentStatus });
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'openai': return 'bg-blue-600/20 text-blue-300';
      case 'google': return 'bg-red-600/20 text-red-300';
      case 'flux': return 'bg-green-600/20 text-green-300';
      case 'flux_pro': return 'bg-purple-600/20 text-purple-300';
      case 'flux_ultra': return 'bg-yellow-600/20 text-yellow-300';
      case 'shuttle': return 'bg-pink-600/20 text-pink-300';
      case 'stability': return 'bg-orange-600/20 text-orange-300';
      default: return 'bg-gray-600/20 text-gray-300';
    }
  };

  // Provider options hidden from users as per requirement
  const getProviderOptions = () => [
    { value: 'provider-1', label: 'Provider 1' },
    { value: 'provider-2', label: 'Provider 2' },
    { value: 'provider-3', label: 'Provider 3' },
    { value: 'provider-4', label: 'Provider 4' },
    { value: 'provider-5', label: 'Provider 5' },
  ];

  const getCategoryOptions = () => [
    { value: 'openai', label: 'OpenAI' },
    { value: 'google', label: 'Google' },
    { value: 'flux', label: 'FLUX' },
    { value: 'flux_pro', label: 'FLUX Pro' },
    { value: 'flux_ultra', label: 'FLUX Ultra' },
    { value: 'shuttle', label: 'Shuttle' },
    { value: 'stability', label: 'Stability AI' },
  ];

  const getPlanOptions = () => [
    { value: 'ultra', label: 'Ultra' },
    { value: 'pro', label: 'Pro' },
    { value: 'normal', label: 'Normal' },
  ];

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'ultra': return 'bg-purple-600/20 text-purple-300';
      case 'pro': return 'bg-blue-600/20 text-blue-300';
      case 'normal': return 'bg-green-600/20 text-green-300';
      default: return 'bg-gray-600/20 text-gray-300';
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Settings className="w-8 h-8 mx-auto mb-4 animate-pulse text-blue-400" />
            <p className="text-white">Loading models...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-white flex items-center">
            <Settings className="w-6 h-6 mr-2" />
            Model Management ({models.length} models)
          </CardTitle>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Model
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Add Model Form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-white/10 rounded-lg border border-white/20">
            <h3 className="text-white font-medium mb-4">Add New Model</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Model Key (e.g., provider-1/model-name)"
                value={newModel.model_key}
                onChange={(e) => setNewModel({...newModel, model_key: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                placeholder="Display Name"
                value={newModel.display_name}
                onChange={(e) => setNewModel({...newModel, display_name: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                placeholder="Description"
                value={newModel.description || ''}
                onChange={(e) => setNewModel({...newModel, description: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              {/* Provider field removed from user interface as per requirement */}
              <Select value={newModel.category} onValueChange={(value) => setNewModel({...newModel, category: value})}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {getCategoryOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Priority (1=highest, 100=default)"
                value={newModel.priority}
                onChange={(e) => setNewModel({...newModel, priority: parseInt(e.target.value) || 100})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                type="number"
                placeholder="Max Batch Size"
                value={newModel.max_batch_size}
                onChange={(e) => setNewModel({...newModel, max_batch_size: parseInt(e.target.value) || 4})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Select value={newModel.subscription_plan || 'normal'} onValueChange={(value) => setNewModel({...newModel, subscription_plan: value})}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Subscription Plan" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {getPlanOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddModel} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Save Model
              </Button>
              <Button onClick={() => setShowAddForm(false)} className="bg-gray-600 hover:bg-gray-700">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Models Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-gray-300">Model</TableHead>
                <TableHead className="text-gray-300">Provider (Admin Only)</TableHead>
                <TableHead className="text-gray-300">Category</TableHead>
                <TableHead className="text-gray-300">Plan</TableHead>
                <TableHead className="text-gray-300">Priority</TableHead>
                <TableHead className="text-gray-300">Batch Size</TableHead>
                <TableHead className="text-gray-300">Status</TableHead>
                <TableHead className="text-gray-300">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {models
                .sort((a, b) => {
                  // Sort by priority first (lower number = higher priority)
                  if (a.priority !== b.priority) {
                    return (a.priority || 100) - (b.priority || 100);
                  }
                  // Then sort by category/provider for grouping
                  if (a.category !== b.category) {
                    return a.category.localeCompare(b.category);
                  }
                  // Finally sort by display name
                  return a.display_name.localeCompare(b.display_name);
                })
                .map((model) => (
                <TableRow key={model.id} className="border-white/10">
                  <TableCell>
                    {editingModel === model.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editForm.model_key || ''}
                          onChange={(e) => setEditForm({...editForm, model_key: e.target.value})}
                          className="bg-white/10 border-white/20 text-white text-sm"
                          placeholder="Model key"
                        />
                        <Input
                          value={editForm.display_name || ''}
                          onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                          className="bg-white/10 border-white/20 text-white text-sm"
                          placeholder="Display name"
                        />
                      </div>
                    ) : (
                      <div>
                        <p className="text-white font-medium">
                          {model.display_name}
                          {(model.priority === 1) && <Badge className="ml-2 bg-yellow-600/20 text-yellow-300 text-xs">DEFAULT</Badge>}
                        </p>
                        <p className="text-gray-400 text-sm">{model.model_key}</p>
                        {model.description && <p className="text-gray-500 text-xs">{model.description}</p>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingModel === model.id ? (
                      <div className="text-gray-400 text-sm">
                        Provider: {model.provider}
                        <br />
                        <span className="text-xs">(Internal use only)</span>
                      </div>
                    ) : (
                      <div className="text-gray-400 text-sm">
                        {model.provider.replace('provider-', 'P')}
                        <br />
                        <span className="text-xs text-gray-500">(Internal)</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingModel === model.id ? (
                      <Select 
                        value={editForm.category || model.category} 
                        onValueChange={(value) => setEditForm({...editForm, category: value})}
                      >
                        <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          {getCategoryOptions().map(option => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={getCategoryBadgeColor(model.category)}>
                        {model.category.replace('_', ' ')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingModel === model.id ? (
                      <Select 
                        value={editForm.subscription_plan || model.subscription_plan || 'normal'} 
                        onValueChange={(value) => setEditForm({...editForm, subscription_plan: value})}
                      >
                        <SelectTrigger className="w-28 bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          {getPlanOptions().map(option => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={getPlanBadgeColor(model.subscription_plan || 'normal')}>
                        {(model.subscription_plan || 'normal').toUpperCase()}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingModel === model.id ? (
                      <Input
                        type="number"
                        value={editForm.priority || model.priority || 100}
                        onChange={(e) => setEditForm({...editForm, priority: parseInt(e.target.value) || 100})}
                        className="w-20 bg-white/10 border-white/20 text-white"
                        placeholder="Priority"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-white">{model.priority || 100}</span>
                        {(model.priority === 1) && <Badge className="bg-yellow-600/20 text-yellow-300 text-xs">DEFAULT</Badge>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingModel === model.id ? (
                      <Input
                        type="number"
                        value={editForm.max_batch_size || model.max_batch_size}
                        onChange={(e) => setEditForm({...editForm, max_batch_size: parseInt(e.target.value) || 1})}
                        className="w-20 bg-white/10 border-white/20 text-white"
                      />
                    ) : (
                      <span className="text-white">{model.max_batch_size}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={model.is_active ? 'bg-green-600/20 text-green-300' : 'bg-red-600/20 text-red-300'}>
                      {model.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {editingModel === model.id ? (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSaveModel}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleCancelEdit}
                            className="bg-gray-600 hover:bg-gray-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleEditModel(model)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => toggleModelStatus(model.id, model.is_active)}
                            className={model.is_active ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                          >
                            {model.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleDeleteModel(model.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {models.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">No models configured.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminModelManagement;
